import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Post, EhcpTag, EhcpIndication, TextSource, AiMetadata, EXCLUSION_REASON_LABELS, EXCLUSION_REASON_NEEDS_NOTE } from '../models/post';
import { TaxonomyPayload, TaxonomyTag } from '../models/taxonomy';
import { PostDataService, getPostConflict, PostConflict, EnrichOutcome } from '../services/postDataService';
import { useAppState } from '../context/AppStateContext';
import { makeBaseName, putModifiedVariant } from '../services/uploadHelpers';
import { formatDate } from '../services/sharedService';
import PhotoRedactor from './PhotoRedactor';
import ConfirmationModal from './ConfirmationModal';
import FixTypos from './FixTypos';
import { useOfflineActionTitle } from '../hooks/useConnectivity';

interface Props {
  post: Post;                          // the draft being reviewed (carries ai_metadata)
  onClose: () => void;
  onPublished: (message: string) => void;  // parent reloads posts + toasts
  /** Reviewing an already-published post offers Unpublish — parent reloads and
   *  toasts, leaving the overlay open on the now-draft post so the reviewer
   *  can make their change and republish in one sitting. */
  onUnpublished: (message: string) => void;
  /** Refresh posts from the server — used after saving a mid-review blur so
   *  `post.updated_at` and upload URLs stay current for the next save/publish. */
  onReloadPosts: (skipConfirm?: boolean) => Promise<void>;
  /** Step to the neighbouring post in the list the reviewer is working
   *  through. Omitted at each end of that list, which disables the button. */
  onPrev?: () => void;
  onNext?: () => void;
  /** Where this post sits in that list, for the counter between the buttons.
   *  `index` is zero-based; omitted when the post has dropped out of the
   *  current filter and has no place in it. */
  position?: { index: number; total: number };
}

const INDICATIONS: EhcpIndication[] = ['strength', 'emerging', 'need'];

// Whether a published post's text and EHCP focus areas stay editable in this
// overlay. False locks the published record completely — the reviewer must
// unpublish to change anything, matching how photos/blur/exclusion already
// behave (and how the API already behaves for the post body). Flip to true to
// restore in-place editing; the publish endpoint accepts it either way, so no
// server change is needed to switch back.
// Typed rather than inferred as a literal, so neither value makes the branches
// that read it look statically dead to the compiler or the linter.
export const ALLOW_EDITING_PUBLISHED_POSTS: boolean = false;

// Below this many words we don't auto-call enrichment when the overlay opens — a
// few words rarely has anything worth rewriting/tagging, so skip the spend. The
// reviewer can still force it with the "Re-run AI" button.
const AUTO_ENRICH_MIN_WORDS = 5;

// Is the enrich response the same suggestions the posts list already holds?
// Both sides come out of the same server-side desanitise path, so a structural
// compare is sound. Only used to decide whether the list needs refreshing — a
// false "different" costs one refetch, never correctness.
const sameAiMeta = (a: AiMetadata | null, b: AiMetadata | null): boolean =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

// Slug prefix → SEND Code of Practice area label.
const AREA_LABELS: Record<string, string> = {
  'communication-interaction': 'Communication & Interaction',
  'cognition-learning': 'Cognition & Learning',
  'semh': 'Social, Emotional & Mental Health',
  'sensory-physical': 'Sensory & Physical',
};

// Why the AI produced nothing, in the reviewer's terms. Without this every
// reason — the feature being off, the post being too short, the AI reading it
// and declining — arrived as the same blank line, so there was no way to tell
// a misconfigured server from a post the AI simply had nothing to add to.
// Outcomes that can't coincide with an empty rewrite fall through to the
// generic line below.
const ENRICH_OUTCOME_NOTE: Partial<Record<EnrichOutcome, string>> = {
  skipped_disabled: 'AI suggestions are switched off on this server.',
  skipped_no_text: 'There’s no text on this post for the AI to work from.',
  skipped_not_found: 'This post couldn’t be found when the suggestions were requested.',
  skipped_heuristic: 'This post is too short for the AI to suggest anything.',
  skipped_triage: 'The AI read this post and had nothing to suggest.',
  skipped_debounce: 'Suggestions were generated moments ago. Try again shortly.',
  skipped_unchanged: 'Nothing was suggested last time this text was processed. Edit the text to run it again.',
};

const INDICATION_STYLE: Record<EhcpIndication, { bg: string; fg: string }> = {
  strength: { bg: '#dff0d8', fg: '#1e7e34' },
  emerging: { bg: '#fff3cd', fg: '#9a6b00' },
  need:     { bg: '#fde2e1', fg: '#a02d2c' },
};

// Small in-place popover — closes on outside click or Escape. Used for both
// the per-chip indication dropdown and the per-area "add tag" picker so
// neither needs a portal; they scroll with the panel they're anchored in.
const ClickAwayPopover: React.FC<{
  onClose: () => void;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}> = ({ onClose, className, style, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  return <div ref={ref} className={className} style={style}>{children}</div>;
};

const ReviewPublishOverlay: React.FC<Props> = ({
  post, onClose, onPublished, onUnpublished, onReloadPosts, onPrev, onNext, position,
}) => {
  const { childProfiles, entitlements } = useAppState();
  // Whether this organisation's plan includes EHCP focus areas. Gates the tray
  // AND the tag seeding below — an org downgraded from full still holds
  // historical ai_metadata.ehcp, and seeding from it while the tray is hidden
  // would publish focus areas the reviewer never saw and could not edit.
  const focusAreasEnabled = entitlements.aiFullEnabled;
  // AI suggestions are produced lazily when this overlay opens (see effect below),
  // so ai_metadata is usually empty at mount. Hold it in state so the enrich
  // response can populate the rewrite + tag suggestions live.
  const [aiMeta, setAiMeta] = useState<AiMetadata | null>(post.ai_metadata ?? null);
  const aiRewrite = (aiMeta?.professional_summary ?? '').trim();
  const originalText = (post.text_content ?? '').trim();
  // Skip the on-open auto-enrich for very short posts (see AUTO_ENRICH_MIN_WORDS).
  const postWordCount = originalText ? originalText.split(/\s+/).filter(Boolean).length : 0;
  const autoEnrich = postWordCount >= AUTO_ENRICH_MIN_WORDS;

  // When re-publishing a post that was previously published (then unpublished),
  // pre-populate from the approved record so the reviewer sees exactly what was
  // last approved rather than starting from scratch.
  const prevPublishedText = (post.published_text ?? '').trim();
  const prevPublishedSource: TextSource = post.published_text_source ?? 'original';
  const initialSource: TextSource = prevPublishedText ? prevPublishedSource : 'original';
  const initialText = prevPublishedText || originalText;

  // The published record is read-only in this overlay (see the constant). Every
  // affordance that would change what's live is withheld rather than disabled —
  // the only route to a change is Unpublish, which stays in the ribbon.
  const locked = post.is_published && !ALLOW_EDITING_PUBLISHED_POSTS;

  const [taxonomy, setTaxonomy] = useState<TaxonomyPayload | null>(null);
  const [taxonomyError, setTaxonomyError] = useState(false);
  const [source, setSource] = useState<TextSource>(initialSource);
  const [text, setText] = useState<string>(initialText);
  // Previously-published tags survive a downgrade (they are the published
  // record, not a suggestion), but AI suggestions do not — without the tray
  // there is no way to review them.
  const initialTags: EhcpTag[] = (
    (prevPublishedText ? post.published_categories : null)
    ?? (focusAreasEnabled ? post.ai_metadata?.ehcp : null)
    ?? []
  ).map(t => ({ area: t.area, indication: t.indication }));
  const [selectedTags, setSelectedTags] = useState<EhcpTag[]>(initialTags);
  const [publishing, setPublishing] = useState(false);
  const [confirmingUnpublish, setConfirmingUnpublish] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  // Publishing is the safeguarding gate: while the server can't be reached it
  // shouldn't even look clickable.
  const offlineTitle = useOfflineActionTitle();
  const [conflict, setConflict] = useState<PostConflict | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Lazy enrichment state. `enriching` starts true — we kick off an enrich on open.
  // `tagsTouchedRef` guards against re-seeding tags from AI once the reviewer has
  // curated them (never clobber hand-picked tags). A ref, not state, so the async
  // enrich callback sees the live value even if it resolves mid-edit.
  const [enriching, setEnriching] = useState(autoEnrich);
  const [enrichError, setEnrichError] = useState(false);
  const [enrichOutcome, setEnrichOutcome] = useState<EnrichOutcome | null>(null);
  const tagsTouchedRef = useRef(false);

  // ── Photo viewer + in-place blur ─────────────────────────────────────
  const [viewingUploadId, setViewingUploadId] = useState<number | null>(null);
  const [blurEditorOpen, setBlurEditorOpen] = useState(false);
  const [savingBlur, setSavingBlur] = useState(false);
  const [blurPreviewUrl, setBlurPreviewUrl] = useState<string | null>(null);
  const [confirmingRemoveBlur, setConfirmingRemoveBlur] = useState(false);

  // ── Exclude-from-report (per photo, non-destructive) ────────────────
  const [exclusionFormOpen, setExclusionFormOpen] = useState(false);
  const [exclusionReason, setExclusionReason] = useState<string | null>(null);
  const [exclusionNote, setExclusionNote] = useState('');
  const [savingExclusion, setSavingExclusion] = useState(false);

  // A reason is required: the exclusion is recorded for staff who weren't in
  // the room, and one with no reason is a photo pulled from a child's report
  // with no trace of why. "Other" alone says nothing, so it takes the note.
  const exclusionNoteRequired = exclusionReason === EXCLUSION_REASON_NEEDS_NOTE;
  const canConfirmExclusion = !!exclusionReason && (!exclusionNoteRequired || exclusionNote.trim().length > 0);

  const openPhoto = (uploadId: number) => {
    setViewingUploadId(uploadId);
    setExclusionFormOpen(false);
    setExclusionReason(null);
    setExclusionNote('');
  };

  // Pre-publish confirmation that photos have been checked for privacy issues.
  // Not asked for on an already-published post: blur and exclusion are locked
  // server-side while published, so the photos can't have changed since the
  // check that gated the original publish — there'd be nothing left to catch.
  const [photosReviewed, setPhotosReviewed] = useState(false);
  const photoCheckRequired = !post.is_published && (post.uploads?.length ?? 0) > 0;

  // Discard-changes guard — compare against the pre-populated starting point so
  // Cancel doesn't warn when nothing has actually been changed this session.
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [confirmingSourceSwitch, setConfirmingSourceSwitch] = useState<TextSource | null>(null);
  const isDirty = text.trim() !== initialText.trim() || source !== initialSource;

  // On a published post the stakes of an unsaved edit are higher — the live
  // report keeps showing the old version until "Update Published Post" is
  // pressed — so the ribbon flags tag edits too, not just text/source. Kept
  // separate from `isDirty` so the Cancel discard-guard behaviour is unchanged.
  const tagSignature = (tags: EhcpTag[]) => tags.map(t => `${t.area}:${t.indication}`).sort().join('|');
  const publishedRecordDirty = isDirty || tagSignature(selectedTags) !== tagSignature(initialTags);

  // Same helper — and so the same format — as the "Recorded" date in the header
  // above, which this sits directly beneath: two dates in one piece of chrome
  // written two different ways invited comparing them and mis-reading one.
  // It carries the TIME as well, which a date alone couldn't: posts are
  // routinely published the same day they're recorded, and on the day itself
  // "2 Aug 2026" answered neither "has this been done yet?" nor "was it before
  // or after the edit I'm looking at?".
  const publishedOn = post.published_at ? formatDate(post.published_at) : null;
  // "by <name>", like the Recorded row above, rather than another dot: dots read
  // as a list of peers, and these aren't — one is when, the other is who. Only
  // the wording is borrowed, not the header's muted/semibold pair, because the
  // ribbon paints its own ink (green, or amber when dirty) and text-muted grey
  // would fight it.
  const publishedBy = post.published_by_name ? `by ${post.published_by_name}` : null;
  // Reads "Published for reporting · 02 Aug 2026 14:37 by Sarah Kaur". Either
  // half is dropped if the API didn't send it, so nothing dangles.
  const publishedDetail = [publishedOn, publishedBy].filter(Boolean).join(' ');

  // Leaving this post — closing the overlay, or stepping to another, which
  // remounts it and drops the edits just as surely. Both go through the same
  // guard: the pending action is held so the confirmation can carry it out,
  // rather than the dialog assuming it was always a close.
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null);

  const requestLeave = (action?: () => void) => {
    if (!action) return;
    if (isDirty) {
      // Stored behind a function so React treats it as a value, not an updater.
      setPendingLeave(() => action);
      setConfirmingDiscard(true);
      return;
    }
    action();
  };

  const handleClose = () => requestLeave(onClose);

  // Alt-modified, not the bare arrows: the body of this overlay is a textarea
  // and a set of tag controls, where left and right belong to whatever has
  // focus. Alt keeps the shortcut available while editing rather than making
  // it depend on where the caret happens to be.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); requestLeave(onPrev); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); requestLeave(onNext); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onPrev, onNext, isDirty]);

  useEffect(() => {
    let active = true;
    PostDataService.getTaxonomy()
      .then(t => { if (active) setTaxonomy(t); })
      .catch(() => { if (active) setTaxonomyError(true); });
    return () => { active = false; };
  }, []);

  // Lazy enrichment: generate the rewrite + tag suggestions when the overlay opens
  // (enrich-if-stale — free if the post text is unchanged since the last run). Seed
  // the EHCP tag picker from the AI suggestions only when starting a fresh review
  // (not re-reviewing a previously-published post, whose tags come from its saved
  // published_categories).
  useEffect(() => {
    let active = true;
    if (!autoEnrich) { setEnriching(false); return () => { active = false; }; }
    setEnriching(true);
    setEnrichError(false);
    PostDataService.enrich(post.id)
      .then(({ outcome, metadata }) => {
        if (!active) return;
        // Recorded even when there's no metadata — that's the case it explains.
        setEnrichOutcome(outcome);
        // Refresh the list whenever the server's metadata differs from the copy
        // the list gave us, which is what this overlay seeds from. Leaving it
        // stale is what makes a reopen show the previous rewrite and then
        // visibly replace it a moment later.
        //
        // Deliberately not keyed on outcome === 'enriched'. The list is fetched
        // when the child/date is chosen, and enrichment is lazy — it happens
        // here, on open — so any run after the first reports skipped_unchanged
        // while still handing back metadata the list never saw. Comparing is
        // what catches that; equal metadata refetches nothing.
        if (!sameAiMeta(metadata, post.ai_metadata ?? null)) void onReloadPosts(true);
        if (!metadata) return;
        setAiMeta(metadata);
        if (focusAreasEnabled && !prevPublishedText && !tagsTouchedRef.current) {
          setSelectedTags((metadata.ehcp ?? []).map(t => ({ area: t.area, indication: t.indication })));
        }
      })
      .catch(() => { if (active) setEnrichError(true); })
      .finally(() => { if (active) setEnriching(false); });
    return () => { active = false; };
    // Runs once per post; prevPublishedText is derived from the same post.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.id]);

  // Manual "Re-run AI" — primarily a retry if the on-open enrich failed, or to pick
  // up a draft edited elsewhere. Never overwrites tags the reviewer has curated.
  const handleReRunAi = async () => {
    setEnriching(true);
    setEnrichError(false);
    try {
      const { outcome, metadata } = await PostDataService.enrich(post.id);
      setEnrichOutcome(outcome);
      if (!sameAiMeta(metadata, post.ai_metadata ?? null)) void onReloadPosts(true);
      if (metadata) {
        setAiMeta(metadata);
        if (focusAreasEnabled && !prevPublishedText && !tagsTouchedRef.current) {
          setSelectedTags((metadata.ehcp ?? []).map(t => ({ area: t.area, indication: t.indication })));
        }
      }
    } catch {
      setEnrichError(true);
    } finally {
      setEnriching(false);
    }
  };

  // The chosen source's text as it stood when it was chosen — the yardstick for
  // "has the reviewer edited this?". Frozen on purpose. Deriving it from the
  // live aiRewrite meant a refreshed rewrite moved the baseline underneath
  // untouched text: the panel started reporting "· edited", Fix typos came back
  // on verbatim AI wording, and the false edit rode into the publish payload as
  // provenance for something no human did.
  const [sourceBaseline, setSourceBaseline] = useState<string>(initialText);
  const sourceEdited = text.trim() !== sourceBaseline.trim();

  // The AI has since produced wording that differs from the copy this review is
  // working from — worth surfacing, because nothing else would say so: the left
  // card silently shows the new text while the right box keeps the old.
  const aiRewriteMoved =
    source === 'ai_rewrite' && aiRewrite !== '' && aiRewrite !== sourceBaseline.trim();

  // A "rewrite" identical to the teacher's words isn't one. There's nothing to
  // choose, and publishing it as `ai_rewrite` would credit the AI in the record
  // for wording it left alone — so the card reports and offers no action.
  const aiRewriteIsUnchanged = aiRewrite !== '' && aiRewrite === originalText;

  // The AI rewrite arrives already clean, so checking it verbatim has nothing
  // to find. The moment the reviewer edits it — or picks the teacher's own
  // words instead — the check earns its place back.
  const textIsUntouchedAiRewrite = source === 'ai_rewrite' && !sourceEdited;

  // Wording that exists nowhere but this box — it matches neither raw source.
  // Both sources are already trimmed at their definition. This is stricter than
  // `sourceEdited`, which only looks at the source in use: text typed to match
  // the *other* source counts as edited but is recoverable by switching, so it
  // isn't bespoke and doesn't need guarding.
  // The frozen baseline counts as recoverable too: on a published post it holds
  // the wording that was published, so re-applying a refreshed rewrite over it
  // isn't destroying anything the reviewer wrote.
  const textIsBespoke =
    text.trim() !== originalText &&
    text.trim() !== aiRewrite &&
    text.trim() !== sourceBaseline.trim();

  // Switching baseline reloads that source's text (the reviewer picks a source,
  // then edits). Their edits to the previous source are dropped on switch.
  const applySource = (s: TextSource) => {
    const next = s === 'ai_rewrite' ? aiRewrite : originalText;
    setSource(s);
    setText(next);
    setSourceBaseline(next);
  };

  // Confirm before a switch that destroys something. Only bespoke wording
  // qualifies: text matching either raw source can be got back by switching,
  // but text matching neither has nowhere to come back from — the baselines are
  // the raw texts, never `published_text`. Drafts switch straight away; nothing
  // there is live yet.
  const chooseSource = (s: TextSource) => {
    // The "In use" card stays clickable and normally must not wipe edits. The
    // exception is a rewrite that has moved on: re-picking it is the only way
    // to pull the new wording in.
    if (s === source && !(s === 'ai_rewrite' && aiRewriteMoved)) return;
    if (post.is_published && textIsBespoke) { setConfirmingSourceSwitch(s); return; }
    applySource(s);
  };

  const areas = useMemo(() => {
    const out: { key: string; label: string; tags: TaxonomyTag[] }[] = [];
    if (!taxonomy) return out;
    const groups: Record<string, TaxonomyTag[]> = {};
    for (const tag of taxonomy.ehcp_domains) {
      const key = tag.slug.split('.')[0];
      (groups[key] = groups[key] || []).push(tag);
    }
    return Object.entries(groups).map(([key, tags]) => ({ key, label: AREA_LABELS[key] ?? key, tags }));
  }, [taxonomy]);

  const tagFor = (slug: string) => selectedTags.find(t => t.area === slug);

  // Selected tags render as chips, grouped under their SEND area. Each area
  // has its own "+ Add" popover (scoped to that area's remaining tags), and
  // each chip has its own indication-picker popover — only one of either
  // kind is open at a time.
  const [addPopoverOpen, setAddPopoverOpen] = useState(false);
  const [indicationPopoverFor, setIndicationPopoverFor] = useState<string | null>(null);

  // Collapsed by default: the tray shows the tags, the editor stays folded.
  const [tagsExpanded, setTagsExpanded] = useState(false);

  // The selection flattened into statutory area order, with taxonomy labels
  // attached. The collapsed summary reads across all four areas at once, so it
  // can't lean on the per-area grouping the editor uses.
  const selectedInOrder = useMemo(() => {
    const indicationBySlug = new Map(selectedTags.map(t => [t.area, t.indication]));
    const out: { slug: string; label: string; definition?: string; indication: EhcpIndication }[] = [];
    for (const area of areas) {
      for (const tag of area.tags) {
        const indication = indicationBySlug.get(tag.slug);
        if (indication) out.push({ slug: tag.slug, label: tag.label, definition: tag.definition, indication });
      }
    }
    return out;
  }, [areas, selectedTags]);

  const addTag = (slug: string) => {
    tagsTouchedRef.current = true;
    setSelectedTags(prev => (prev.some(t => t.area === slug) ? prev : [...prev, { area: slug, indication: 'need' } as EhcpTag]));
    setAddPopoverOpen(false);
  };

  const removeTag = (slug: string) => {
    tagsTouchedRef.current = true;
    setSelectedTags(prev => prev.filter(t => t.area !== slug));
  };

  const setIndication = (slug: string, indication: EhcpIndication) => {
    tagsTouchedRef.current = true;
    setSelectedTags(prev => prev.map(t => (t.area === slug ? { ...t, indication } : t)));
    setIndicationPopoverFor(null);
  };

  const safeguardingLabels = useMemo(() => {
    const slugs = aiMeta?.safeguarding ?? [];
    if (slugs.length === 0) return [] as string[];
    const bySlug = new Map((taxonomy?.safeguarding ?? []).map(t => [t.slug, t.label]));
    return slugs.map(s => bySlug.get(s) ?? s);
  }, [aiMeta, taxonomy]);

  // The published side of this screen never says what the photo outcome is, so
  // the tray label states it rather than just counting: with exclusions in
  // play, "4 photos" overstates what actually reaches the report.
  const excludedPhotoCount = post.uploads?.filter(u => u.excluded_from_report === true).length ?? 0;

  const handlePublish = async () => {
    if (!text.trim()) { setError('The published text cannot be empty.'); return; }
    setPublishing(true);
    setError(null);
    setConflict(null);
    try {
      await PostDataService.publish(post.id, {
        finalText: text,
        textSource: source,
        sourceEdited,
        finalCategories: selectedTags,
        expectedUpdatedAt: post.updated_at,
      });
      onPublished('Post published to report.');
    } catch (err) {
      const c = getPostConflict(err);
      if (c) setConflict(c);
      else setError('Failed to publish. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    setConfirmingUnpublish(false);
    setUnpublishing(true);
    setError(null);
    try {
      await PostDataService.unpublish(post.id);
      onUnpublished('Post unpublished — make your changes and republish when ready.');
    } catch (err) {
      setError('Failed to unpublish. Please try again.');
    } finally {
      setUnpublishing(false);
    }
  };

  // Apply a face-blur to one upload mid-review. Unlike the post editor
  // (PostsGrid), this screen has no separate "Save" step, so the redacted
  // variant is uploaded and persisted immediately via the same
  // PostDataService.updatePost path the editor uses.
  const handleApplyBlur = async (redacted: File) => {
    const uploadId = viewingUploadId;
    if (uploadId == null) return;
    setBlurEditorOpen(false);

    const preview = URL.createObjectURL(redacted);
    setBlurPreviewUrl(preview);
    setSavingBlur(true);
    setError(null);
    setConflict(null);
    try {
      // No placeholder: a made-up id was sent to the API, refused as
      // "child does not belong to your organisation", and now raises a
      // security alert. Say what actually went wrong instead.
      const childUniqueId = childProfiles.find(c => c.id === post.child_ids[0])?.unique_id;
      if (!childUniqueId) {
        URL.revokeObjectURL(preview);
        setBlurPreviewUrl(null);
        setError("Couldn't save the blur: this post's child isn't in the loaded child list (archived, or still loading). Reload and try again.");
        return; // finally still clears the saving state
      }
      const baseName = makeBaseName(childUniqueId, post.post_date);
      const { modified_s3_key, modified_s3_key_thumbnail } = await putModifiedVariant(redacted, baseName, childUniqueId);

      const updatedUploads = (post.uploads ?? []).map(u =>
        u.id === uploadId
          ? { ...u, modified_s3_key, modified_s3_key_thumbnail, display_modified: true }
          : u
      );
      await PostDataService.updatePost(post.id, { ...post, uploads: updatedUploads }, post.updated_at);
      await onReloadPosts(true);
      setBlurPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    } catch (err) {
      URL.revokeObjectURL(preview);
      setBlurPreviewUrl(null);
      const c = getPostConflict(err);
      if (c) setConflict(c);
      else setError('Failed to save the blurred photo. Please try again.');
    } finally {
      setSavingBlur(false);
    }
  };

  // Drop the blurred variant so the photo shows its original again. Persisted
  // immediately for the same reason as handleApplyBlur, and written the same
  // way the post editor's "Remove blur" saves: modified keys cleared.
  const handleRemoveBlur = async () => {
    const uploadId = viewingUploadId;
    setConfirmingRemoveBlur(false);
    if (uploadId == null) return;

    setSavingBlur(true);
    setError(null);
    setConflict(null);
    try {
      const updatedUploads = (post.uploads ?? []).map(u =>
        u.id === uploadId
          ? { ...u, modified_s3_key: null, modified_s3_key_thumbnail: null, display_modified: true }
          : u
      );
      await PostDataService.updatePost(post.id, { ...post, uploads: updatedUploads }, post.updated_at);
      await onReloadPosts(true);
    } catch (err) {
      const c = getPostConflict(err);
      if (c) setConflict(c);
      else setError('Failed to remove the blur. Please try again.');
    } finally {
      setSavingBlur(false);
    }
  };

  // Exclude a photo from the report (or bring it back). Same immediate-persist
  // pattern as the blur: the uploads array carries the exclusion fields and the
  // API stores them on the post↔upload link — the upload itself is untouched.
  const handleSetExclusion = async (uploadId: number, exclude: boolean) => {
    setExclusionFormOpen(false);
    setSavingExclusion(true);
    setError(null);
    setConflict(null);
    try {
      const updatedUploads = (post.uploads ?? []).map(u =>
        u.id === uploadId
          ? {
              ...u,
              excluded_from_report: exclude,
              exclusion_reason: exclude ? exclusionReason : null,
              exclusion_note: exclude ? (exclusionNote.trim() || null) : null,
            }
          : u
      );
      await PostDataService.updatePost(post.id, { ...post, uploads: updatedUploads }, post.updated_at);
      await onReloadPosts(true);
      setExclusionReason(null);
      setExclusionNote('');
    } catch (err) {
      const c = getPostConflict(err);
      if (c) setConflict(c);
      else setError(exclude ? 'Failed to exclude the photo. Please try again.' : 'Failed to restore the photo. Please try again.');
    } finally {
      setSavingExclusion(false);
    }
  };

  const viewingUpload = post.uploads?.find(u => u.id === viewingUploadId) ?? null;

  // Stepping through the post's photos from inside the enlargement, in the same
  // order as the thumbnail strip. Held while a blur is saving or its editor is
  // open: the enlargement paints `blurPreviewUrl` over whatever it is showing,
  // so moving on mid-save would lay one photo's pending blur across another.
  const viewableUploads = post.uploads ?? [];
  const viewingIndex = viewableUploads.findIndex(u => u.id === viewingUploadId);
  const canStepPhotos = !savingBlur && !blurEditorOpen && viewingIndex >= 0;
  const stepPhoto = (delta: number) => {
    if (!canStepPhotos) return;
    const next = viewableUploads[viewingIndex + delta];
    if (next) setViewingUploadId(next.id);
  };

  // Bare arrows are safe here in a way they aren't in the overlay behind: the
  // enlargement takes the whole screen and has nothing to type into.
  useEffect(() => {
    if (!viewingUpload) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); stepPhoto(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); stepPhoto(1); }
      else if (e.key === 'Escape') { e.preventDefault(); setViewingUploadId(null); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingUpload, viewingIndex, canStepPhotos, viewableUploads.length]);

  return (
    <>
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 1080,
        background: 'rgba(0, 0, 0, 0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
      }}
    >
      <div
        className="card"
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '1100px', height: '88vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#fffdf8',
        }}
      >
        {/* Header */}
        <div
          className="card-header d-flex align-items-center justify-content-between"
          style={{ background: 'transparent', borderBottom: '1px solid rgba(38,146,63,0.22)' }}
        >
          {/* When the post was recorded, grouped with the title because it's
              part of what's being reviewed — next to the close button it read
              as modal furniture (a save time or similar). "Recorded" earns its
              place: an unlabelled date here is ambiguous against the published
              ribbon below, which carries the date it was PUBLISHED. The value
              is the same string the list card behind this modal shows.

              Which report period the post lands in is decided by this date, so
              publishing without it on screen is publishing blind. */}
          <div className="d-flex align-items-center gap-5" style={{ minWidth: 0 }}>
            <h4 className="mb-0">Review &amp; Publish</h4>
            <span className="d-flex align-items-center gap-2 text-nowrap">
              <i className="fas fa-calendar-day" style={{ color: '#26923f' }} aria-hidden="true" />
              <span className="text-muted">Recorded</span>
              <time className="fw-semibold" dateTime={post.post_date}>
                {formatDate(post.post_date)}
              </time>
              {/* Who wrote it, beside when — both are provenance of the thing
                  being reviewed rather than modal furniture. The reviewer is
                  often not the author, and knowing whose words these are is
                  part of judging them. Omitted rather than shown as "Unknown"
                  when the API didn't send a name: a missing author is a gap in
                  the record, not a fact about the post. */}
              {post.created_by_name && (
                <>
                  <span className="text-muted">by</span>
                  <span className="fw-semibold">{post.created_by_name}</span>
                </>
              )}
            </span>
          </div>
          <button className="close-button" onClick={handleClose} title="Close"><i className="fas fa-times" /></button>
        </div>

        {/* Published ribbon — the post's live status has to be unmissable, so it
            sits above the scrolling body rather than only in the footer. The
            header above it is transparent per the app's card convention, which
            leaves this the only tinted band in the chrome — that's what carries
            the prominence. While there are unsaved edits it escalates to amber
            in place: same bar, changed condition, no layout shift. */}
        {post.is_published && (
          <div
            className="d-flex align-items-center justify-content-between gap-3 px-3 py-2"
            style={{
              background: publishedRecordDirty ? 'rgba(255,193,7,0.18)' : 'rgba(38,146,63,0.14)',
              borderBottom: '1px solid rgba(38,146,63,0.22)',
              borderLeft: `4px solid ${publishedRecordDirty ? '#c9970a' : '#26923f'}`,
            }}
          >
            <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
              <i
                className={`fas fa-${publishedRecordDirty ? 'triangle-exclamation' : 'circle'}`}
                style={{ color: publishedRecordDirty ? '#a37500' : '#26923f', fontSize: publishedRecordDirty ? '0.95rem' : '0.6rem' }}
                aria-hidden="true"
              />
              <div className="text-truncate" style={{ color: publishedRecordDirty ? '#7a5c00' : '#1e6b31', lineHeight: 1.3 }}>
                <span className="fw-semibold">
                  {publishedRecordDirty ? 'Unsaved changes' : 'Published for reporting'}
                </span>
                {publishedRecordDirty ? (
                  <span style={{ opacity: 0.85 }}> · The report still shows the published version.</span>
                ) : publishedDetail && (
                  <span style={{ opacity: 0.85 }}> · {publishedDetail}</span>
                )}
                {/* Stays green: the published record is intact and live, so
                    amber would overstate it. This is an offer, not a problem. */}
                {aiRewriteMoved && (
                  <span style={{ opacity: 0.85 }}> · The AI now suggests different wording.</span>
                )}
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              {/* Locked, there's nothing to take the wording into — the note
                  opposite stays, because it's a reason to unpublish, and that
                  button is right here. */}
              {aiRewriteMoved && !locked && (
                <button
                  type="button"
                  className="ribbon-pill"
                  onClick={() => chooseSource('ai_rewrite')}
                  disabled={publishing || unpublishing}
                  title="Replace the published text with the AI's current wording"
                >
                  <i className="fas fa-wand-magic-sparkles" aria-hidden="true" />
                  Use updated text
                </button>
              )}
              <button
                type="button"
                className={`ribbon-pill${publishedRecordDirty ? ' ribbon-pill--alert' : ''}`}
                data-help="publish.unpublish"
                onClick={() => setConfirmingUnpublish(true)}
                disabled={publishing || unpublishing || !!offlineTitle}
                title={offlineTitle ?? 'Revert this post to draft and remove it from the report'}
              >
                <i className="fas fa-rotate-left" aria-hidden="true" />
                {unpublishing ? 'Unpublishing…' : 'Unpublish'}
              </button>
            </div>
          </div>
        )}

        {conflict && (
          <div className="alert alert-warning m-3 mb-0" role="alert">
            This post was edited{conflict.updated_by_name ? <> by <strong>{conflict.updated_by_name}</strong></> : ''}{' '}
            since you opened it. Close and reopen it to review the latest before publishing.
          </div>
        )}
        {error && <div className="alert alert-danger m-3 mb-0" role="alert">{error}</div>}

        {/* Body: original (left) | published record (right) */}
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* LEFT — read-only sources; pick a starting point with "Use this".
              A stack of three: the safeguarding alert and the photo tray are
              pinned top and bottom, and only the text sources between them
              scroll. Both pinned items carry a consequence, and a long post
              used to push them out of sight. */}
          <div style={{ width: '46%', borderRight: '1px solid rgba(38,146,63,0.18)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Safeguarding (read-only awareness) — an alert belongs where it's
                read first, not below however much text the teacher wrote. */}
            {safeguardingLabels.length > 0 && (
              <div className="alert alert-warning m-3 mb-0" role="alert" style={{ fontSize: '0.9em', flexShrink: 0 }}>
                <i className="fas fa-triangle-exclamation me-1" />
                <strong>Possible safeguarding indicator{safeguardingLabels.length > 1 ? 's' : ''}:</strong>{' '}
                {safeguardingLabels.join(', ')}.
                <div className="small mt-1">Follow your safeguarding process — not included in the report.</div>
              </div>
            )}

            {/* Flex column so the source cards can size against it — their
                max-height is a percentage of this zone. */}
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column' }}>
            <div className="text-muted small text-uppercase fw-semibold mb-2">
              Reference — choose a starting point
            </div>

            {/* Teacher's original */}
            <div className={`source-card mb-3${source === 'original' ? ' is-selected' : ''}`}>
              <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                <span className="source-card__label">Teacher's original</span>
                {/* Locked: the source choice is part of the published record, so
                    this column is reference only. "In use" still renders — it
                    says where the published wording came from, which is the one
                    thing worth knowing here. */}
                {(!locked || source === 'original') && (
                  <button
                    type="button"
                    className={`choice-pill${source === 'original' ? ' is-selected' : ''}`}
                    onClick={() => chooseSource('original')}
                    aria-pressed={source === 'original'}
                    disabled={locked}
                  >
                    {source === 'original'
                      ? <><i className="fas fa-check" aria-hidden="true" />In use</>
                      : 'Use this'}
                  </button>
                )}
              </div>
              <p className="mb-0 small source-card__text" style={{ whiteSpace: 'pre-line' }}>
                {originalText || <span className="text-muted">No text.</span>}
              </p>
            </div>

            {/* AI rewrite */}
            {aiRewrite ? (
              <div className={`source-card is-ai mb-3${source === 'ai_rewrite' ? ' is-selected' : ''}`}>
                <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                  <span className="source-card__label">✨ AI rewrite</span>
                  {/* No "Use this" when there's nothing to switch to. Still
                      shows "In use" if this is already the source — that's
                      state, and suppressing it would hide where the text came
                      from. */}
                  {(locked ? source === 'ai_rewrite' : (source === 'ai_rewrite' || !aiRewriteIsUnchanged)) && (
                    <button
                      type="button"
                      className={`choice-pill${source === 'ai_rewrite' ? ' is-selected' : ''}${aiRewriteMoved && !locked ? ' is-actionable' : ''}`}
                      onClick={() => chooseSource('ai_rewrite')}
                      aria-pressed={source === 'ai_rewrite'}
                      disabled={locked}
                    >
                      {/* "In use" would be a dead end once the rewrite has
                          moved on — the card shows wording the published text
                          doesn't have, so the button has to offer taking it.
                          Locked, taking it isn't on offer at all, so the pill
                          stays plain state. */}
                      {source !== 'ai_rewrite'
                        ? 'Use this'
                        : aiRewriteMoved && !locked
                          ? 'Use updated text'
                          : <><i className="fas fa-check" aria-hidden="true" />In use</>}
                    </button>
                  )}
                </div>
                {aiRewriteIsUnchanged ? (
                  <p className="mb-0 small text-muted">
                    No changes suggested — this matches the teacher's original word for word.
                  </p>
                ) : (
                  <p className="mb-0 small source-card__text" style={{ whiteSpace: 'pre-line' }}>{aiRewrite}</p>
                )}
              </div>
            ) : enriching ? (
              <div className="border rounded p-2 mb-3 small text-muted d-flex align-items-center gap-2">
                <span className="spinner-border spinner-border-sm" />
                ✨ Generating AI suggestions…
              </div>
            ) : enrichError ? (
              /* Both of these normally point at "Re-run AI", which is hidden on
                 a published post — so don't send the reviewer after a button
                 that isn't on screen. */
              <div className="small text-danger mb-3">
                {post.is_published
                  ? 'Couldn’t load the AI suggestions for this post.'
                  : 'Couldn’t generate AI suggestions. Use “Re-run AI” to try again, or publish the teacher’s original text.'}
              </div>
            ) : !autoEnrich ? (
              <div className="small text-muted mb-3">
                {post.is_published
                  ? 'This post is too short for automatic AI suggestions.'
                  : 'This post is too short for automatic AI suggestions. Use “Re-run AI” if you’d like to generate them.'}
              </div>
            ) : (
              <div className="small text-muted mb-3">
                {(enrichOutcome && ENRICH_OUTCOME_NOTE[enrichOutcome]) ?? 'No AI rewrite available for this post.'}
              </div>
            )}

            {/* Sits under whichever AI state is showing — the rewrite, an
                error, or a "none available" note — because it acts on all of
                them. Hidden while published: the post text is locked
                server-side, so enrich-if-stale has identical input and hands
                back what it handed back last time. */}
            {!post.is_published && (
              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  className="ai-pill"
                  onClick={handleReRunAi}
                  disabled={enriching}
                  title="Regenerate the AI suggestions for this post"
                >
                  {enriching
                    ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />Working…</>
                    : <><i className="fas fa-arrows-rotate" aria-hidden="true" />Re-run AI</>}
                </button>
              </div>
            )}
            </div>

            {/* Photos — click to enlarge; badge shows if a blur has been
                applied. Pinned below the scroll: the footer asks the reviewer
                to confirm they've reviewed every photo, so every photo has to
                stay on screen for that to mean anything. */}
            {post.uploads && post.uploads.length > 0 && (
              <div className="review-photo-tray">
                <div className="review-photo-tray__label">
                  {post.uploads.length} photo{post.uploads.length === 1 ? '' : 's'}
                  {excludedPhotoCount > 0 && ` · ${excludedPhotoCount} excluded from the report`}
                </div>
                <div className="review-photo-tray__grid">
                {post.uploads.map(u => {
                  const showingModified = u.display_modified ?? true;
                  const isExcluded = u.excluded_from_report === true;
                  return (
                    <div
                      key={u.id}
                      className="photo-tile"
                      style={{ width: 90, height: 90, cursor: 'pointer', opacity: isExcluded ? 0.55 : 1 }}
                      onClick={() => openPhoto(u.id)}
                      title={isExcluded
                        ? `Excluded from the report${u.exclusion_reason ? ` — ${EXCLUSION_REASON_LABELS[u.exclusion_reason] ?? u.exclusion_reason}` : ''}. ${post.is_published ? 'Click to view.' : 'Click to view or restore.'}`
                        : 'Click to enlarge'}
                    >
                      <img src={u.thumbnail_url || u.url} alt={u.file_name} style={isExcluded ? { filter: 'grayscale(100%)' } : undefined} />
                      {isExcluded ? (
                        <span className="tile-badge" style={{ background: 'rgba(38,36,32,0.85)' }}>
                          <i className="fas fa-ban" />
                          Excluded
                        </span>
                      ) : u.has_modified && (
                        <span
                          className="tile-badge"
                          style={{ background: showingModified ? 'rgba(42,107,58,0.95)' : 'rgba(176,48,48,0.95)' }}
                          title={showingModified ? 'The blurred version is shown' : 'The original is shown (blur hidden)'}
                        >
                          <i className={`fas fa-${showingModified ? 'user-secret' : 'eye'}`} />
                          {showingModified ? 'Blurred' : 'Original'}
                        </span>
                      )}
                    </div>
                  );
                })}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT — the published text (editable) + tags. Plain white against
              the overlay's warm card: the left column is reference material,
              this is the record itself. */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: '#fff' }}>
            {/* Flex column so the textarea can take whatever the heading, the
                Fix-typos row and any open suggestion panel leave behind. */}
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column' }}>
            <div className="d-flex align-items-center justify-content-between mb-2" style={{ flexShrink: 0 }}>
              <div className="text-muted small text-uppercase fw-semibold">Published text</div>
              <div className="small text-muted">
                {/* Says the record is closed, next to the provenance that
                    describes it — the textarea below looks inert, but only
                    this explains why. */}
                {locked && (
                  <><i className="fas fa-lock me-1" aria-hidden="true" />Locked · </>
                )}
                from <strong>{source === 'ai_rewrite' ? 'AI rewrite' : 'original'}</strong>{sourceEdited ? ' · edited' : ''}
              </div>
            </div>

            {/* Fills the column rather than sitting at a fixed 8 rows. The
                min-height is the floor it gives back to the suggestion panel
                when that opens, so the two never fight over the space. */}
            {/* Read-only rather than swapped for a static block: the published
                text keeps the same flex sizing and its own scroll, which a
                plain div in this column would lose on a long post. */}
            <textarea
              className="form-control mb-2 post-text-area"
              style={{ flex: 1, minHeight: '10rem', ...(locked ? { background: '#f6f5f1', cursor: 'default' } : null) }}
              value={text}
              onChange={e => setText(e.target.value)}
              readOnly={locked}
              aria-readonly={locked}
            />

            {!locked && !textIsUntouchedAiRewrite && (
              <FixTypos
                key={post.id}
                text={text}
                postId={post.id}
                childIds={post.child_ids ?? []}
                onAccept={setText}
              />
            )}
            </div>

            {/* Focus areas — pinned tray, mirroring the photo tray opposite.
                Collapsed it still shows the tags themselves; only the editing
                machinery folds away. These go onto a child's EHCP record, so
                they should never be one click from unseen.

                Absent entirely on a plan without full AI: there are no tags to
                review, and an empty tray would read as something broken rather
                than something not included. The text column takes the height. */}
            {focusAreasEnabled && (
            <div className="review-tag-tray">
              <div className="review-tag-tray__head">
                <span className="review-tag-tray__label">Focus areas (EHCP)</span>
                {/* No editor while locked — the chips below are already the
                    read-only view, so the tray needs nothing in its place. */}
                {taxonomy && !locked && (
                  <button
                    type="button"
                    className="tray-pill"
                    onClick={() => { setTagsExpanded(v => !v); setAddPopoverOpen(false); setIndicationPopoverFor(null); }}
                    aria-expanded={tagsExpanded}
                  >
                    <i className={`fas fa-${tagsExpanded ? 'check' : selectedInOrder.length === 0 ? 'plus' : 'pen'}`} aria-hidden="true" />
                    {tagsExpanded ? 'Done' : selectedInOrder.length === 0 ? 'Add focus areas' : 'Edit'}
                  </button>
                )}
              </div>

              <div className="review-tag-tray__body">
              {taxonomyError && <div className="small text-danger">Couldn't load the focus-area list.</div>}
              {!taxonomy && !taxonomyError && <div className="small text-muted">Loading focus areas…</div>}

              {/* Collapsed — the answer, read-only. */}
              {taxonomy && !tagsExpanded && (
                selectedInOrder.length === 0 ? (
                  <span className="small text-muted">No focus areas tagged yet.</span>
                ) : (
                  <div className="review-tag-tray__chips">
                    {selectedInOrder.map(t => {
                      const st = INDICATION_STYLE[t.indication];
                      return (
                        <span
                          key={t.slug}
                          className="ehcp-chip"
                          style={{ background: st.bg, color: st.fg, borderColor: st.fg }}
                          title={t.definition}
                        >
                          <span className="fw-semibold">{t.label}</span>
                          <span style={{ opacity: 0.85, textTransform: 'capitalize' }}>· {t.indication}</span>
                        </span>
                      );
                    })}
                  </div>
                )
              )}

              {/* Expanded — the editor. Only areas that hold tags get a row;
                  the empty ones used to render a heading, an italic "None
                  tagged" and their own Add button apiece. The grouping now
                  lives in the single picker, where it aids navigation instead
                  of drawing four empty shelves. */}
              {taxonomy && tagsExpanded && (
                <>
            {areas.map(area => {
              const selectedInArea = area.tags.filter(tag => tagFor(tag.slug));
              if (selectedInArea.length === 0) return null;
              return (
                <div key={area.key} className="mb-2">
                  <div className="text-muted small mb-1">{area.label}</div>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    {selectedInArea.map(tag => {
                      const sel = tagFor(tag.slug)!;
                      const st = INDICATION_STYLE[sel.indication];
                      return (
                        <span
                          key={tag.slug}
                          className="d-inline-flex align-items-center rounded-pill"
                          style={{ position: 'relative', background: st.bg, color: st.fg, border: `1px solid ${st.fg}`, fontSize: '0.82rem', lineHeight: 1.2 }}
                        >
                          <button
                            type="button"
                            className="btn btn-sm border-0 py-1 ps-2 pe-1 d-inline-flex align-items-center gap-1"
                            style={{ background: 'transparent', color: 'inherit', fontSize: 'inherit' }}
                            onClick={() => setIndicationPopoverFor(indicationPopoverFor === tag.slug ? null : tag.slug)}
                            aria-haspopup="true"
                            aria-expanded={indicationPopoverFor === tag.slug}
                            title={tag.definition}
                          >
                            <span className="fw-semibold">{tag.label}</span>
                            <span style={{ opacity: 0.85, textTransform: 'capitalize' }}>· {sel.indication}</span>
                            <i className="fas fa-caret-down" style={{ fontSize: '0.75em' }} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm border-0 py-1 px-2"
                            style={{ background: 'transparent', color: 'inherit' }}
                            onClick={() => removeTag(tag.slug)}
                            aria-label={`Remove ${tag.label}`}
                            title="Remove"
                          >
                            <i className="fas fa-times" />
                          </button>

                          {indicationPopoverFor === tag.slug && (
                            <ClickAwayPopover
                              onClose={() => setIndicationPopoverFor(null)}
                              className="rounded shadow-sm"
                              /* Opens upward — the tray is pinned to the foot
                                 of the column, so a downward popover would be
                                 clipped by the overlay's edge. */
                              style={{
                                position: 'absolute', bottom: 'calc(100% + 4px)', left: 0, zIndex: 20,
                                background: '#fff', border: '1px solid rgba(0,0,0,0.15)', minWidth: 140, overflow: 'hidden',
                              }}
                            >
                              {INDICATIONS.map(ind => {
                                const indSt = INDICATION_STYLE[ind];
                                const on = sel.indication === ind;
                                return (
                                  <button
                                    key={ind}
                                    type="button"
                                    className="btn btn-sm w-100 text-start d-flex align-items-center gap-2"
                                    style={{ borderRadius: 0, color: indSt.fg, background: on ? indSt.bg : 'transparent', fontWeight: on ? 600 : 400, textTransform: 'capitalize' }}
                                    onClick={() => setIndication(tag.slug, ind)}
                                  >
                                    <i className={`fas fa-check ${on ? '' : 'invisible'}`} style={{ fontSize: '0.8em' }} />
                                    {ind}
                                  </button>
                                );
                              })}
                            </ClickAwayPopover>
                          )}
                        </span>
                      );
                    })}

                  </div>
                </div>
              );
            })}

                  {/* One picker for all four areas. The grouping moves inside
                      it, where it helps you find a tag rather than drawing a
                      heading for every area you aren't using. */}
                  <span style={{ position: 'relative' }}>
                    <button
                      type="button"
                      className="tray-pill"
                      onClick={() => setAddPopoverOpen(!addPopoverOpen)}
                      aria-haspopup="true"
                      aria-expanded={addPopoverOpen}
                      disabled={selectedTags.length === taxonomy?.ehcp_domains.length}
                    >
                      <i className="fas fa-plus" aria-hidden="true" />
                      Add focus area
                    </button>

                    {addPopoverOpen && (
                      <ClickAwayPopover
                        onClose={() => setAddPopoverOpen(false)}
                        className="rounded shadow-sm p-2"
                        style={{
                          position: 'absolute', bottom: 'calc(100% + 4px)', left: 0, zIndex: 20,
                          background: '#fff', border: '1px solid rgba(0,0,0,0.15)', width: 300,
                          maxHeight: '16rem', overflowY: 'auto',
                        }}
                      >
                        {areas.map(area => {
                          const availableInArea = area.tags.filter(tag => !tagFor(tag.slug));
                          if (availableInArea.length === 0) return null;
                          return (
                            <div key={area.key} className="mb-2">
                              <div className="text-muted small mb-1">{area.label}</div>
                              <div className="d-flex flex-wrap gap-1">
                                {availableInArea.map(tag => (
                                  <button
                                    key={tag.slug}
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary rounded-pill"
                                    title={tag.definition}
                                    onClick={() => addTag(tag.slug)}
                                  >
                                    {tag.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </ClickAwayPopover>
                    )}
                  </span>
                </>
              )}
              </div>
            </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          className="p-3"
          style={{
            background: 'linear-gradient(0deg, rgba(250,248,241,0.96), rgba(250,248,241,0.85))',
            borderTop: '1px solid rgba(38,146,63,0.22)',
          }}
        >
          {photoCheckRequired && (
            <div
              className="d-flex justify-content-end align-items-center gap-2 mb-2 px-3 py-2 rounded"
              data-help="publish.photo-check"
              style={{ background: '#fff3cd', border: '1px solid #ffc107' }}
            >
              <i className="fas fa-triangle-exclamation" style={{ color: '#856404' }} />
              <label className="form-check-label small fw-semibold" htmlFor="photosReviewedCheck" style={{ color: '#856404', cursor: 'pointer' }}>
                I've reviewed all photos in this post
              </label>
              <input
                className="form-check-input mt-0"
                type="checkbox"
                id="photosReviewedCheck"
                checked={photosReviewed}
                onChange={e => setPhotosReviewed(e.target.checked)}
                style={{ flexShrink: 0 }}
              />
            </div>
          )}
          {/* Unpublish lives in the published ribbon at the top; the tag count
              and source are both already visible in the panel above, so the
              footer carries nothing but the two decisions. */}
          <div className="d-flex justify-content-between align-items-center gap-3">
            {/* Moving through the queue belongs with finishing a post, not up
                in the header: you read, you decide, you move on, and all three
                should be in the same place. Held at the far left so a mis-click
                can't land on Publish, and quiet, because it's the step you take
                *after* the decision rather than one of the decisions. */}
            <div className="rpo-nav" role="group" aria-label="Move through the post list">
              <button
                type="button"
                className="rpo-nav__btn"
                onClick={() => requestLeave(onPrev)}
                disabled={!onPrev || publishing || unpublishing}
                title="Previous post (Alt + ←)"
                aria-label="Previous post"
              >
                <i className="fas fa-chevron-left" />
              </button>
              {position && (
                <span className="rpo-nav__count">
                  {position.index + 1} of {position.total}
                </span>
              )}
              <button
                type="button"
                className="rpo-nav__btn"
                onClick={() => requestLeave(onNext)}
                disabled={!onNext || publishing || unpublishing}
                title="Next post (Alt + →)"
                aria-label="Next post"
              >
                <i className="fas fa-chevron-right" />
              </button>
            </div>

            {/* Locked, nothing here can be dirty and there's nothing to save,
                so the footer carries one exit and it isn't called "Cancel" —
                there's no edit to cancel. Unpublish is in the ribbon. */}
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary" onClick={handleClose} disabled={publishing || unpublishing}>
                {locked ? 'Close' : 'Cancel'}
              </button>
              {!locked && (
                <button
                  className="btn btn-primary"
                  data-help="publish.button"
                  onClick={handlePublish}
                  disabled={publishing || unpublishing || !!offlineTitle || !text.trim() || (photoCheckRequired && !photosReviewed)}
                  title={offlineTitle}
                >
                  {publishing ? 'Publishing…' : post.is_published ? 'Update Published Post' : 'Publish to Report'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

      {viewingUpload && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed', inset: 0, zIndex: 1090,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
          }}
          onClick={(e) => { e.stopPropagation(); setViewingUploadId(null); }}
        >
          <div onClick={e => e.stopPropagation()} style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
            <img
              src={blurPreviewUrl ?? viewingUpload.url}
              alt={viewingUpload.file_name}
              style={{
                maxWidth: '90vw', maxHeight: '78vh', display: 'block', borderRadius: 8,
                filter: viewingUpload.excluded_from_report ? 'grayscale(100%) brightness(0.85)' : undefined,
              }}
            />
            <button
              type="button"
              className="lightbox-close"
              style={{ position: 'absolute', top: 8, right: 8 }}
              onClick={() => setViewingUploadId(null)}
              title="Close"
              aria-label="Close photo"
            >
              <i className="fas fa-times" />
            </button>

            {/* Step through the post's photos without going back to the strip.
                Disabled at each end rather than wrapping: on a screen whose job
                is checking every photo, "have I seen them all" needs to stay
                answerable. */}
            {viewableUploads.length > 1 && (
              <>
                <button
                  type="button"
                  className="lightbox-nav lightbox-nav--prev"
                  onClick={() => stepPhoto(-1)}
                  disabled={!canStepPhotos || viewingIndex === 0}
                  title="Previous photo (←)"
                  aria-label="Previous photo"
                >
                  <i className="fas fa-chevron-left" />
                </button>
                <button
                  type="button"
                  className="lightbox-nav lightbox-nav--next"
                  onClick={() => stepPhoto(1)}
                  disabled={!canStepPhotos || viewingIndex === viewableUploads.length - 1}
                  title="Next photo (→)"
                  aria-label="Next photo"
                >
                  <i className="fas fa-chevron-right" />
                </button>
                <span className="lightbox-count">
                  {viewingIndex + 1} of {viewableUploads.length}
                </span>
              </>
            )}

            {/* Excluded banner — why this photo won't appear in the report */}
            {viewingUpload.excluded_from_report && (
              <div style={{ position: 'absolute', top: 12, left: 0, right: 0, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
                <div
                  className="px-3 py-2 rounded"
                  style={{ background: 'rgba(38,36,32,0.88)', color: '#fff', fontSize: '0.85rem', maxWidth: '80%' }}
                >
                  <i className="fas fa-ban me-2" />
                  <strong>Excluded from the report</strong>
                  {viewingUpload.exclusion_reason && (
                    <> — {EXCLUSION_REASON_LABELS[viewingUpload.exclusion_reason] ?? viewingUpload.exclusion_reason}</>
                  )}
                  {viewingUpload.exclusion_note && (
                    <div className="small mt-1" style={{ opacity: 0.85 }}>“{viewingUpload.exclusion_note}”</div>
                  )}
                </div>
              </div>
            )}

            {post.is_published ? (
              /* Published posts are locked server-side (PUT is draft authoring
                 only) — blur and exclusion changes need an unpublish first, so
                 don't offer actions that can only 403. */
              <div style={{ position: 'absolute', bottom: 12, left: 0, right: 0, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
                <div
                  className="px-3 py-2 rounded"
                  style={{ background: 'rgba(38,36,32,0.88)', color: '#fff', fontSize: '0.85rem' }}
                >
                  <i className="fas fa-lock me-2" />
                  Locked while published — unpublish the post to change its photos.
                </div>
              </div>
            ) : exclusionFormOpen ? (
              /* Reason picker + optional note, confirmed with "Exclude photo" */
              <div style={{ position: 'absolute', bottom: 12, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
                {/* Header / body / footer like the app's other dialogs, but
                    anchored over the photo rather than centred: the reviewer is
                    looking at the image to decide, so it stays in view. */}
                <div className="modal-content ivory-panel exclusion-form" style={{ width: 460, maxWidth: '86vw' }} onClick={e => e.stopPropagation()}>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="fas fa-ban text-danger me-2" aria-hidden="true" />
                      Exclude this photo from the report?
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={() => setExclusionFormOpen(false)}
                    />
                  </div>

                  <div className="modal-body">
                    <p className="small text-muted mb-3">
                      The photo stays on the post — it just won't appear in the report.
                    </p>

                    {/* Pills, not buttons: these pick a value, and reading as
                        buttons put four things that don't act directly above two
                        that do. Same control as the source cards' — repainted in
                        ink by .exclusion-form. */}
                    <div className="d-flex flex-wrap gap-1 mb-3">
                      {Object.entries(EXCLUSION_REASON_LABELS).map(([slug, label]) => (
                        <button
                          key={slug}
                          type="button"
                          className={`choice-pill${exclusionReason === slug ? ' is-selected' : ''}`}
                          onClick={() => setExclusionReason(exclusionReason === slug ? null : slug)}
                          aria-pressed={exclusionReason === slug}
                        >
                          {exclusionReason === slug && <i className="fas fa-check" aria-hidden="true" />}{label}
                        </button>
                      ))}
                    </div>

                    <textarea
                      className="form-control form-control-sm"
                      rows={2}
                      placeholder={exclusionNoteRequired
                        ? 'Say why this photo is being left out'
                        : 'Optional note — why is this photo being left out?'}
                      value={exclusionNote}
                      onChange={e => setExclusionNote(e.target.value)}
                    />
                  </div>

                  <div className="modal-footer">
                    {/* me-auto rather than justify-content-between: .modal-footer
                        is flex-end, and an auto margin wins without fighting it.
                        Says what's outstanding so the disabled button doesn't
                        have to explain itself. */}
                    <span className="small text-muted me-auto">
                      {!exclusionReason
                        ? 'Choose a reason to continue.'
                        : exclusionNoteRequired && !exclusionNote.trim()
                          ? 'Add a note to continue.'
                          : ''}
                    </span>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setExclusionFormOpen(false)}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-dark"
                      disabled={savingExclusion || !canConfirmExclusion}
                      data-help="publish.exclude-photo"
                      onClick={() => handleSetExclusion(viewingUpload.id, true)}
                    >
                      {savingExclusion ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</> : <><i className="fas fa-ban me-1" />Exclude photo</>}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ position: 'absolute', bottom: 12, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 10 }}>
                {viewingUpload.excluded_from_report ? (
                  <button
                    type="button"
                    className="btn btn-light"
                    disabled={savingExclusion}
                    onClick={() => handleSetExclusion(viewingUpload.id, false)}
                  >
                    {savingExclusion ? (
                      <><span className="spinner-border spinner-border-sm me-2" />Saving…</>
                    ) : (
                      <><i className="fas fa-rotate-left me-1" />Include in report</>
                    )}
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn btn-light"
                      disabled={savingBlur}
                      onClick={() => setBlurEditorOpen(true)}
                    >
                      {savingBlur ? (
                        <><span className="spinner-border spinner-border-sm me-2" />Saving…</>
                      ) : (
                        <><i className="fas fa-user-secret me-1" />{viewingUpload.has_modified ? 'Re-blur faces' : 'Blur faces'}</>
                      )}
                    </button>
                    {viewingUpload.has_modified && (
                      <button
                        type="button"
                        className="btn btn-light"
                        disabled={savingBlur || savingExclusion}
                        onClick={() => setConfirmingRemoveBlur(true)}
                      >
                        <i className="fas fa-eraser me-1" />Remove blur
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-light"
                      disabled={savingBlur || savingExclusion}
                      onClick={() => setExclusionFormOpen(true)}
                      title="Keep the photo on the post but leave it out of the report"
                    >
                      <i className="fas fa-ban me-1" />Exclude from report
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {blurEditorOpen && viewingUpload && (
        <PhotoRedactor
          show
          imageUrl={viewingUpload.original_url || viewingUpload.url}
          onCancel={() => setBlurEditorOpen(false)}
          onApply={handleApplyBlur}
        />
      )}

      <ConfirmationModal
        show={confirmingRemoveBlur}
        title="Remove the blur?"
        message="The blurred version will be discarded and the post will show the original photo. Blurring it again means starting over."
        confirmText="Remove blur"
        cancelText="Cancel"
        onConfirm={handleRemoveBlur}
        onCancel={() => setConfirmingRemoveBlur(false)}
        zIndex={1200}
      />

      <ConfirmationModal
        show={confirmingUnpublish}
        title="Unpublish post?"
        message="The post will revert to draft and be excluded from reports until it's reviewed and published again."
        confirmText="Yes, unpublish"
        cancelText="Cancel"
        onConfirm={handleUnpublish}
        onCancel={() => setConfirmingUnpublish(false)}
        zIndex={1200}
      />

      <ConfirmationModal
        show={confirmingSourceSwitch != null}
        title="Replace the edited text?"
        message={`The text you've edited will be replaced with the ${confirmingSourceSwitch === 'ai_rewrite' ? 'AI rewrite' : "teacher's original"} exactly as written. Switching back won't return it.`}
        confirmText="Replace text"
        cancelText="Keep current text"
        onConfirm={() => { if (confirmingSourceSwitch) applySource(confirmingSourceSwitch); setConfirmingSourceSwitch(null); }}
        onCancel={() => setConfirmingSourceSwitch(null)}
        zIndex={1200}
      />

      <ConfirmationModal
        show={confirmingDiscard}
        title="Discard changes?"
        message="You've made changes to the published text. If you leave this post now, those changes will be lost."
        confirmText="Discard changes"
        cancelText="Keep editing"
        onConfirm={() => {
          const leave = pendingLeave;
          setConfirmingDiscard(false);
          setPendingLeave(null);
          (leave ?? onClose)();
        }}
        onCancel={() => { setConfirmingDiscard(false); setPendingLeave(null); }}
        zIndex={1200}
      />
    </>
  );
};

export default ReviewPublishOverlay;
